import { Button } from '@chakra-ui/button'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import { Icon, Text as ChakraText, VStack } from '@chakra-ui/react'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { AiOutlineUserAdd } from 'react-icons/ai'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import type { UserErrorResponse } from '../../interfaces'
import { UserContext } from '../../providers'

interface Props {
  childAccountClientNumber: string
  userId: string
  isOpen: boolean
  onClose(): void
  getData(): Promise<void>
}

const AddNewAgencyChildUserModal = ({
  childAccountClientNumber,
  userId,
  isOpen,
  onClose,
  getData,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const { user } = useContext(UserContext)

  const toast = useToast()

  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <>
          <ModalHeader>
            <Icon
              as={AiOutlineUserAdd}
              color={colors.appBattleshipGrey}
              backgroundColor={colors.appPaleGrey}
              boxSize={6}
              padding="0.1em"
              borderRadius="50%"
              marginRight="0.5em"
            />
            {t('delete_user_account', 'Delete User Account')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} alignItems="flex-start">
              <ChakraText>
                {t('agency_child_delete_user_account_description')}
              </ChakraText>
              <ChakraText>
                {t(
                  'this_change_cannot_be_reversed',
                  'This change cannot be reversed.',
                )}
              </ChakraText>
            </VStack>
          </ModalBody>
          <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
            <Button onClick={onClose} variant="outline">
              {t('cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              onClick={async (): Promise<void> => {
                if (user?.clientNumber) {
                  const response = (await request.delete(
                    `/api/clients/${user.clientNumber}/agency` +
                      `/child/${childAccountClientNumber}/user/${userId}`,
                  )) as UserErrorResponse | {}

                  if (request.error) {
                    toast({
                      description: (response as UserErrorResponse).message,
                      status: 'error',
                      isClosable: true,
                      duration: 5000,
                      position: 'top-right',
                      variant: 'outline',
                    })
                  } else {
                    toast({
                      description: t(
                        'successfully_deleted',
                        'Successfully Deleted',
                      ),
                      status: 'success',
                      isClosable: true,
                      duration: 5000,
                      position: 'top-right',
                      variant: 'outline',
                    })

                    await getData()
                  }

                  onClose()
                }
              }}
            >
              {t('submit', 'Submit')}
            </Button>
          </ModalFooter>
        </>
      </ModalContent>
    </Modal>
  )
}

export default AddNewAgencyChildUserModal
